import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { useApp } from "../components/appState";
import { Flyer } from "../components/Flyer";
import { FlyerField, FlyerSource } from "../components/FlyerTools";
import { Sheet } from "../components/Sheet";
import { readFlyer, searchUrl, storeFlyer, type Picked } from "../lib/flyer";
import { listingShot, loneReel, reelFrom } from "../lib/flyerViewer";
import { dayOfMonth, dow, fLong, plural } from "../lib/format";
import type { Rsvp } from "../lib/types";
import {
  CHIPS,
  RSVPS,
  addUpcoming,
  billOf,
  byMonth,
  editFrom,
  editUpcoming,
  fetchLinked,
  fetchUpcoming,
  hideUpcoming,
  isNew,
  listingReading,
  onCalendar,
  reconcileCalendar,
  setRsvp,
  setUpcomingFlyer,
  type Chip,
  type UpcomingDto,
  type UpcomingEdit,
  type UpcomingExtra,
  type UpcomingRow,
} from "../lib/upcomingPage";

function Field({ label, value, onChange, type = "text", placeholder }: { label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string }) {
  return (
    <label className="f">
      <span>{label}</span>
      <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function EditForm({ e, set, children }: { e: UpcomingEdit; set: (e: UpcomingEdit) => void; children?: ReactNode }) {
  return (
    <div className="form">
      <Field label="Bill, as listed" value={e.event_name} onChange={(v) => set({ ...e, event_name: v })} />
      <div className="two">
        <Field label="Date" type="date" value={e.date} onChange={(v) => set({ ...e, date: v })} />
        <Field label="Venue" value={e.venue} onChange={(v) => set({ ...e, venue: v })} />
      </div>
      <Field label="Event" value={e.event_label} placeholder="Festival or tour (optional)" onChange={(v) => set({ ...e, event_label: v })} />
      <div className="two">
        <Field label="Starts" value={e.start_time} placeholder="8pm" onChange={(v) => set({ ...e, start_time: v })} />
        <Field label="Ends" value={e.end_time} placeholder="optional" onChange={(v) => set({ ...e, end_time: v })} />
      </div>
      {children}
    </div>
  );
}

const flyerSearch = (e: UpcomingEdit) => searchUrl({ event: e.event_label, bands: billOf(e.event_name), venue: e.venue, date: e.date });
/** Named after the date and headliner being saved, so a listing's flyer is stored only when it saves. */
const storeListingFlyer = (p: Picked, e: UpcomingEdit) => storeFlyer(p, billOf(e.event_name)[0] ?? "", e.date, "upcoming");

function EditSheet({ row, onClose }: { row: UpcomingRow; onClose: () => void }) {
  const { changed } = useApp();
  const [e, setE] = useState(editFrom(row));
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [picked, setPicked] = useState<Picked | null>(null);
  const [removed, setRemoved] = useState(false);
  const run = (p: Promise<unknown>, msg: string) =>
    p.then(() => {
      changed(msg);
      onClose();
    }, (x: Error) => setError(x.message));
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // The flyer first: a failed upload then leaves the listing as it was.
      const url = picked ? await storeListingFlyer(picked, e) : null;
      await editUpcoming(row.id, e);
      if (picked || (removed && row.image_url)) await setUpcomingFlyer(row.id, url);
      changed("Saved");
      onClose();
    } catch (x) {
      setError((x as Error).message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Sheet title="Edit listing" label="Edit listing" onClose={onClose}>
      <EditForm e={e} set={setE}>
        <FlyerField url={removed ? null : row.image_url} picked={picked} search={flyerSearch(e)} title={e.event_name}
          onPick={(p) => { setPicked(p); setRemoved(false); }} onRemove={() => { setPicked(null); setRemoved(true); }} />
      </EditForm>
      {error && <p className="error">{error}</p>}
      <div className="sheet-acts">
        <button type="button" className="btn" disabled={saving} onClick={save}>
          Save
        </button>
        <button type="button" className="btn ghost" onClick={onClose}>
          Cancel
        </button>
      </div>
      {confirm ? (
        <div className="confirm">
          <span>Remove this listing? It stays hidden when the watcher sees it again.</span>
          <div className="sheet-acts">
            <button type="button" className="btn danger" onClick={() => run(hideUpcoming(row.id), "Removed")}>
              Remove
            </button>
            <button type="button" className="btn ghost sm" onClick={() => setConfirm(false)}>
              Keep it
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="btn danger" style={{ justifySelf: "start" }} onClick={() => setConfirm(true)}>
          Remove listing
        </button>
      )}
    </Sheet>
  );
}

function AddSheet({ onClose, today }: { onClose: () => void; today: string }) {
  const { changed, zoom } = useApp();
  const [e, setE] = useState<UpcomingEdit>({ event_name: "", date: today, venue: "", event_label: "", start_time: "", end_time: "" });
  const [extra, setExtra] = useState<UpcomingExtra>({ url: "", price: "", rsvp: null });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // As Log a show: start from the flyer, whose reading fills the fields and which is stored on Add.
  const [start, setStart] = useState<"flyer" | "blank">("flyer");
  const [picked, setPicked] = useState<Picked | null>(null);
  const [reading, setReading] = useState<{ notes: string | null } | null>(null);
  const ready = e.event_name.trim() && e.venue.trim() && e.date;
  const takeReading = async (p: Picked) => {
    const r = await readFlyer(p);
    const said = listingReading(r);
    setE((cur) => ({ ...cur, ...said.e }));
    setExtra((cur) => ({ ...cur, ...said.extra }));
    setPicked(p);
    setReading({ notes: r.notes });
  };
  const add = async () => {
    setSaving(true);
    setError(null);
    try {
      const image_url = picked ? await storeListingFlyer(picked, e) : null;
      await addUpcoming({ ...e, ...extra, image_url });
      changed("Added");
      onClose();
    } catch (x) {
      setError((x as Error).message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Sheet title="Add a show" label="Add an upcoming show" onClose={onClose}>
      <div className="seg" role="group" aria-label="Start from">
        <button type="button" aria-pressed={start === "flyer"} onClick={() => { setStart("flyer"); setError(null); }}>From a flyer</button>
        <button type="button" aria-pressed={start === "blank"} onClick={() => { setStart("blank"); setError(null); }}>Blank</button>
      </div>
      {start === "flyer" && !reading && (
        <div className="drop">
          <b>Start from the show's flyer</b>
          <span>The bill, date, venue, price and ticket link are read from it, and it becomes the listing's flyer.</span>
          <FlyerSource primary onPick={takeReading} busyText="Reading the flyer…" />
        </div>
      )}
      {reading && picked && (
        <div className="parsed">
          <Flyer url={picked.preview} alt="The flyer" onZoom={() => zoom(loneReel(picked.preview, "The flyer you picked"))} />
          <div>
            <b>Read from the flyer.</b>
            <br />
            <span className="muted">Check the fields below; the flyer is saved with the listing.</span>
            {reading.notes && <span className="faint" style={{ display: "block", marginTop: 4 }}>It also says: {reading.notes}</span>}
          </div>
        </div>
      )}
      {(start === "blank" || reading) && (
        <>
          <EditForm e={e} set={setE}>
            <div className="two">
              <Field label="Tickets link" value={extra.url} onChange={(v) => setExtra({ ...extra, url: v })} />
              <Field label="Price" value={extra.price} onChange={(v) => setExtra({ ...extra, price: v })} />
            </div>
            <div className="f">
              <span>RSVP</span>
              <span className="rsvp" role="group" aria-label="RSVP">
                {RSVPS.map((r) => (
                  <button type="button" key={r.value} className={r.value} aria-pressed={extra.rsvp === r.value} onClick={() => setExtra({ ...extra, rsvp: extra.rsvp === r.value ? null : r.value })}>
                    {r.label}
                  </button>
                ))}
              </span>
            </div>
            <FlyerField url={null} picked={picked} search={flyerSearch(e)} title={e.event_name} onPick={setPicked} onRemove={() => setPicked(null)} />
          </EditForm>
          {error && <p className="error">{error}</p>}
          <div className="sheet-acts">
            <button type="button" className="btn primary" disabled={!ready || saving} onClick={add}>
              Add show
            </button>
            <button type="button" className="btn ghost" onClick={onClose}>
              Cancel
            </button>
          </div>
        </>
      )}
      {start === "flyer" && !reading && error && <p className="error">{error}</p>}
    </Sheet>
  );
}

function Card({ u, today, onEdit, reel }: { u: UpcomingRow; today: string; onEdit: () => void; reel?: UpcomingRow[] }) {
  const { changed, toast, zoom } = useApp();
  const answer = (r: Exclude<Rsvp, null>) =>
    setRsvp(u.id, u.rsvp === r ? null : r).then(() => changed(), (e: Error) => toast(e.message));
  const meta = [u.venue, u.time, u.price].filter(Boolean).join(" · ");
  return (
    <div className={`uc ${u.rsvp === "no" ? "dim" : ""}`}>
      <div className="uthumb">
        {/* A 54px tile can't print a readable headliner, and the card already names it: show the date instead. */}
        <Flyer
          url={u.image_url}
          alt={`Flyer for ${u.bands[0] ?? u.event_name}`}
          onZoom={() => zoom(reelFrom(reel ?? [u], u, listingShot))}
          fallback={
            <div className="ud" aria-hidden="true">
              <span className="dow">{dow(u.date)}</span>
              <span className="day">{dayOfMonth(u.date)}</span>
            </div>
          }
        />
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="udate mono">{fLong(u.date)}</div>
        <div className="ut">
          {u.url ? (
            <a href={u.url} target="_blank" rel="noopener noreferrer">
              {u.event_name}
            </a>
          ) : (
            u.event_name
          )}
          {isNew(u, today) && <span className="chip" style={{ color: "var(--accent)", borderColor: "#5b4f1c" }}>new</span>}
          {u.event_label && <span className="pill">{u.event_label}</span>}
        </div>
        <div className="us">{meta}</div>
        {u.matched_artist && u.matched_artist !== "manual" && <div className="umatch">Matched {u.matched_artist}</div>}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          <span className="rsvp" role="group" aria-label="RSVP">
            {RSVPS.map((r) => (
              <button type="button" key={r.value} className={r.value} aria-pressed={u.rsvp === r.value} onClick={() => answer(r.value)}>
                {r.label}
              </button>
            ))}
          </span>
          {onCalendar(u) && <span className="cal">✓ in your calendar</span>}
        </div>
        <div style={{ display: "flex", gap: 12, marginTop: 8, fontSize: 12 }}>
          <a className="link muted" href={u.calendar_link} target="_blank" rel="noopener noreferrer">
            + Google Calendar
          </a>
          <button type="button" className="link muted" onClick={onEdit}>
            Edit
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Upcoming() {
  const { version, today, todayInfo } = useApp();
  // Best effort: a failed reconcile leaves the calendar as it was, and the next load retries.
  useEffect(() => {
    reconcileCalendar().catch(() => undefined);
  }, []);
  const [sp, setSp] = useSearchParams();
  const linked = Number(sp.get("show")) || null;
  const chip = (CHIPS.find((c) => c.value === sp.get("f"))?.value ?? "all") as Chip;
  const hideNo = sp.get("no") !== "1";
  const [data, setData] = useState<UpcomingDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<UpcomingRow | null>(null);
  const [adding, setAdding] = useState(false);

  const setParam = useCallback(
    (k: string, v: string | null) =>
      setSp((p) => {
        if (v) p.set(k, v);
        else p.delete(k);
        return p;
      }, { replace: true }),
    [setSp],
  );

  useEffect(() => {
    let live = true;
    setError(null);
    (linked ? fetchLinked(linked) : fetchUpcoming(chip, hideNo))
      .then((d) => live && setData(d))
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [linked, chip, hideNo, version]);

  const waiting = todayInfo?.went.length ?? 0;
  return (
    <>
      <div className="ph">
        <h1>Upcoming</h1>
        {data && !linked && <span className="meta">{data.total} on the radar</span>}
      </div>
      {waiting > 0 && (
        <div className="status-box">
          <span>
            {plural(waiting, "past show")} you RSVP'd to {waiting > 1 ? "are" : "is"} waiting on Shows as <b style={{ color: "var(--accent)" }}>Went?</b>
          </span>
        </div>
      )}
      {linked ? (
        <p className="muted" style={{ margin: 0 }}>
          Showing one listing.{" "}
          <button type="button" className="link" onClick={() => setParam("show", null)}>
            See all upcoming shows
          </button>
        </p>
      ) : (
        <div className="chips">
          {CHIPS.map((c) => (
            <button type="button" key={c.value} aria-pressed={chip === c.value} onClick={() => setParam("f", c.value === "all" ? null : c.value)}>
              {c.label}
            </button>
          ))}
          <button type="button" aria-pressed={hideNo} onClick={() => setParam("no", hideNo ? "1" : null)}>
            Hide not going
          </button>
        </div>
      )}
      {error && <p className="error">{error}</p>}
      {!data && !error && <p className="loading">Loading…</p>}
      {data && (
        <div>
          {data.shows.length ? (
            byMonth(data.shows).map((g) => (
              <div key={g.month}>
                <div className="month">{g.month}</div>
                {g.shows.map((u) => (
                  <Card key={u.id} u={u} today={today} onEdit={() => setEditing(u)} reel={data.shows} />
                ))}
              </div>
            ))
          ) : (
            <p className="muted">Nothing here with these filters.</p>
          )}
        </div>
      )}
      <button type="button" className="btn ghost" style={{ justifySelf: "start" }} onClick={() => setAdding(true)}>
        + Add an upcoming show
      </button>
      {editing && <EditSheet row={editing} onClose={() => setEditing(null)} />}
      {adding && <AddSheet today={today} onClose={() => setAdding(false)} />}
    </>
  );
}
